// "Move to portfolio" / "Remove from portfolio" modal — single loan (loan
// header overflow menu) and bulk (loans list bulk bar) share it. A one-line
// reason is REQUIRED whenever any selected loan currently belongs to a
// portfolio (move or remove); a first assignment from "no portfolio" is
// reasonless. The move itself is idempotent server-side.

import { useState } from "react";
import { Button, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useMoveLoansToPortfolio } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { PortfolioPicker, type PortfolioPickerResult } from "./PortfolioPicker";

interface MoveToPortfolioModalProps {
  loanIds: string[];
  /** True when ANY selected loan currently has a portfolio → reason required. */
  anyCurrentlyAssigned: boolean;
  /** "move" shows the picker; "remove" clears the assignment. */
  mode: "move" | "remove";
  onClose: () => void;
  /** Fired after a successful move with the result counts. */
  onDone: (moved: number, skipped: number) => void;
}

export function MoveToPortfolioModal({
  loanIds,
  anyCurrentlyAssigned,
  mode,
  onClose,
  onDone,
}: MoveToPortfolioModalProps) {
  const [target, setTarget] = useState<PortfolioPickerResult | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const move = useMoveLoansToPortfolio();

  const reasonRequired = anyCurrentlyAssigned;
  const canSubmit =
    (mode === "remove" || target !== null) &&
    (!reasonRequired || reason.trim().length > 0) &&
    !move.isPending;

  async function submit() {
    setError(null);
    try {
      const result = await move.mutateAsync({
        loanIds,
        portfolioId: mode === "remove" ? null : (target?.portfolioId ?? null),
        reason: reason.trim() || undefined,
      });
      onDone(result.movedCount, result.skippedCount);
    } catch (e) {
      setError((e as Error).message ?? "Move failed.");
    }
  }

  const title =
    mode === "remove"
      ? `Remove ${loanIds.length === 1 ? "loan" : `${loanIds.length} loans`} from portfolio`
      : `Move ${loanIds.length === 1 ? "loan" : `${loanIds.length} loans`} to portfolio`;

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={move.isPending}
            disabled={!canSubmit}
            onClick={() => void submit()}
            data-testid="move-portfolio-submit"
          >
            {mode === "remove" ? "Remove" : "Move"}
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-3" data-testid="move-portfolio-modal">
        {mode === "move" && (
          <div className="flex flex-col gap-space-1">
            <span className="text-caption text-gray-mid uppercase tracking-label">
              Portfolio
            </span>
            <PortfolioPicker autoFocus onSelect={setTarget} />
          </div>
        )}
        <div className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid uppercase tracking-label">
            Reason{reasonRequired ? "" : " (optional)"}
          </span>
          <Input
            value={reason}
            placeholder={
              reasonRequired
                ? "Why is this loan leaving its portfolio?"
                : "Optional note"
            }
            onChange={(e) => setReason(e.target.value)}
            data-testid="move-portfolio-reason"
          />
          {reasonRequired && (
            <span className="text-caption text-gray-mid">
              Required — at least one selected loan is leaving an existing
              portfolio.
            </span>
          )}
        </div>
        {error && (
          <p className="text-label text-status-error m-0" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
